import * as React from "react";
import { cn } from "@/utils/theme";
import { Button, buttonVariants, Icon } from "@elaniintc/goes-design-system";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn("flex justify-center", className)}
      {...props}
    />
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex flex-row items-center", className)}
      {...props}
    />
  );
}

function PaginationItem({ ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & React.ComponentProps<typeof Button>;

function PaginationLink({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) {
  return (
    <Button
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      type="button"
      className={cn(
        buttonVariants({
          variant: props.variant,
          group: props.group,
          size,
        }),
        "text-sm font-normal",
        isActive && "bg-neutral-object-accent",
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Ir a la página anterior"
      size="xs"
      group="left"
      className={cn("size-8", className)}
      variant="quiet"
      {...props}
    >
      <Icon
        type="far"
        name="chevron-left"
        className="text-sm"
        aria-hidden="true"
      />
      <span className="sr-only">Página anterior</span>
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Ir a la siguiente página"
      variant="quiet"
      size="xs"
      group="right"
      className={cn("size-8", className)}
      {...props}
    >
      <span className="sr-only">Siguiente página</span>
      <Icon
        type="far"
        name="chevron-right"
        className="text-sm"
        aria-hidden="true"
      />
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn("flex size-8 items-center justify-center", className)}
      {...props}
    >
      <Icon type="fas" name="ellipsis" className="text-sm" aria-hidden="true" />
      <span className="sr-only">Más páginas</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
